import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Send, RefreshCw, AlertTriangle, PackageX, CheckCircle2 } from "lucide-react";

const StockCell = ({ qty, tone, dotColor = "bg-emerald-500", testId }) => {
  if (qty <= 0)
    return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={testId}>
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />0
    </span>;
  return (
    <span className={`inline-flex items-center gap-1.5 font-semibold ${tone}`} data-testid={testId}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotColor}`} />
      {qty.toLocaleString("en-IN")}
    </span>
  );
};

const AlertTable = ({ rows, level, testId }) => (
  <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className={`text-xs uppercase tracking-wider text-white ${level === "red" ? "bg-red-700" : "bg-amber-700"}`}>
        <tr>{["#", "Product", "SKU", "30d Units", "Per Day", "KNQ0 (own)", "WareIQ (3PL)", "FBA (Amazon)", "Days Left"].map((h, i) => (
          <th key={i} className="text-left px-3 py-2.5 font-semibold" colSpan={h === "Product" ? 2 : 1}>{h}</th>))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const total = r.total_stock ?? ((r.own_stock || 0) + (r.wareiq_stock || 0) + (r.fba_stock || 0));
          return (
            <tr key={r.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`alert-row-${r.sku}`}>
              <td className="px-3 py-2 text-xs text-muted-foreground">{r.rank}</td>
              <td className="pl-3 py-2 w-12"><SkuThumb sku={r.sku} alt={r.title} /></td>
              <td className="px-3 py-2 font-medium max-w-[18rem] truncate">{r.title || r.sku}</td>
              <td className="px-3 py-2 font-mono text-xs">{r.sku}</td>
              <td className="px-3 py-2">{r.units_30d.toLocaleString("en-IN")}</td>
              <td className="px-3 py-2">{r.daily_velocity}</td>
              <td className="px-3 py-2" data-testid={`own-${r.sku}`}>
                <StockCell qty={r.own_stock ?? 0} tone={level === "red" ? "text-red-600" : "text-foreground"} dotColor="bg-blue-500" />
              </td>
              <td className="px-3 py-2" data-testid={`wareiq-${r.sku}`}>
                <StockCell qty={r.wareiq_stock ?? 0} tone="text-foreground" dotColor="bg-purple-500" />
              </td>
              <td className="px-3 py-2" data-testid={`fba-${r.sku}`}>
                <StockCell qty={r.fba_stock ?? 0} tone="text-foreground" dotColor="bg-emerald-500" />
              </td>
              <td className="px-3 py-2">{total <= 0 ? "—" : `${r.days_cover}d`}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const StockAlerts = () => {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/tools/stock-alerts")
    .then(({ data }) => setData(data))
    .catch(() => {
      toast.error("Failed to load stock alerts — retry with the refresh button");
      setData((d) => d || { tracked: 0, warehouse: "KNQ0", low_stock_days: 3, red: [], yellow: [], red_count: 0, yellow_count: 0, ok_count: 0, telegram: {} });
    });
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/tools/stock-alerts/telegram");
      toast.success("Stock alert PDF sent to Telegram");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const toggleDaily = async (on) => {
    await api.post("/tools/stock-alerts/daily", { enabled: on });
    toast.success(on ? "Daily stock alert report enabled" : "Daily report disabled");
    load();
  };

  if (!data) return <Skeleton className="h-72" />;

  return (
    <div className="space-y-6" data-testid="stock-alerts-tool">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Tracking your <strong>top {data.tracked} selling listings</strong> (last 30 days) against <strong>total sellable stock</strong> = KNQ0 (own) + WareIQ (3PL) + FBA. Snapshot {data.snapshot_date || "n/a"}.
          🔴 Red = zero across all warehouses · 🟡 Yellow = under {data.low_stock_days} days of sales cover · WareIQ locations: {(data.wareiq_locations || []).join(", ") || "—"}.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Switch checked={data.telegram?.daily_enabled} onCheckedChange={toggleDaily} data-testid="stock-daily-toggle" />
            <span className="text-muted-foreground">Daily at {data.telegram?.send_hour || 9}:00 IST</span>
          </div>
          <Button className="rounded-full" onClick={sendNow} disabled={sending || !data.telegram?.connected} data-testid="stock-send-telegram">
            <Send className="h-4 w-4 mr-1.5" aria-hidden="true" /> {sending ? "Sending…" : "Send PDF to Telegram"}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => { setData(null); load(); }} data-testid="stock-refresh">
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {!data.telegram?.connected && (
        <p className="text-xs text-amber-600" data-testid="stock-tg-warning">Telegram not connected — connect it from the Dashboard tab to send reports.</p>
      )}

      <div className="grid grid-cols-3 gap-4" data-testid="stock-alert-cards">
        <div className="bg-card border border-red-300 dark:border-red-900 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-red-600"><PackageX className="h-4 w-4" aria-hidden="true" /><span className="text-xs font-semibold uppercase tracking-wider">Red — Out of stock</span></div>
          <p className="text-3xl font-semibold mt-1" data-testid="red-count">{data.red_count}</p>
        </div>
        <div className="bg-card border border-amber-300 dark:border-amber-900 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-amber-600"><AlertTriangle className="h-4 w-4" aria-hidden="true" /><span className="text-xs font-semibold uppercase tracking-wider">Yellow — Low stock</span></div>
          <p className="text-3xl font-semibold mt-1" data-testid="yellow-count">{data.yellow_count}</p>
        </div>
        <div className="bg-card border border-green-300 dark:border-green-900 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-green-600"><CheckCircle2 className="h-4 w-4" aria-hidden="true" /><span className="text-xs font-semibold uppercase tracking-wider">OK</span></div>
          <p className="text-3xl font-semibold mt-1" data-testid="ok-count">{data.ok_count}</p>
        </div>
      </div>

      {data.red.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-heading text-base font-semibold tracking-tight text-red-700 dark:text-red-400">🔴 Out of stock ({data.red_count})</h3>
          <AlertTable rows={data.red} level="red" testId="red-alerts-table" />
        </div>
      )}
      {data.yellow.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-heading text-base font-semibold tracking-tight text-amber-700 dark:text-amber-400">🟡 Low stock — under {data.low_stock_days} days cover ({data.yellow_count})</h3>
          <AlertTable rows={data.yellow} level="yellow" testId="yellow-alerts-table" />
        </div>
      )}
      {data.red.length === 0 && data.yellow.length === 0 && (
        <p className="text-sm text-muted-foreground p-6 text-center" data-testid="no-alerts">All top {data.tracked} sellers are sufficiently stocked 🎉</p>
      )}
    </div>
  );
};
